import { Menu } from "@/ui";
import type { SignatureRecord } from "@/lib/api";
import { insertAtPos, type EditorPos } from "@/lib/editor-insert";

/** Extract the TipTap node array from a signature's stored doc. */
export function signatureContentNodes(content: unknown): any[] {
    if (!content || typeof content !== "object") return [];
    const doc = content as { content?: unknown[] };
    return Array.isArray(doc.content) ? doc.content : [];
}

/** Insert a signature's nodes at `pos` (or the current cursor if omitted). */
export function insertSignatureAtCursor(
    editor: any,
    record: SignatureRecord,
    pos?: EditorPos | number | null,
): void {
    if (!editor) return;
    const nodes = signatureContentNodes(record.content);
    if (!nodes.length) return;
    insertAtPos(editor, nodes, pos);
}

interface SignaturePickerProps {
    signatures: SignatureRecord[];
    onPick: (record: SignatureRecord) => void;
    disabled?: boolean;
    /** Called on pointer-down of the trigger, while the editor still has the caret. */
    onCaptureCursor?: () => void;
}

/**
 * Small dropdown that lists the user's named signatures. Picking one inserts
 * it at the cursor (the host wires `onPick` to `insertSignatureAtCursor`).
 * Renders nothing when the library is empty.
 */
export default function SignaturePicker({ signatures, onPick, disabled, onCaptureCursor }: SignaturePickerProps) {
    if (!signatures.length) return null;
    return (
        <Menu.Root>
            <Menu.Trigger
                disabled={disabled}
                className="inline-flex items-center gap-1 text-[13px]"
                onPointerDown={onCaptureCursor}
            >
                Insert signature ▾
            </Menu.Trigger>
            <Menu.Content>
                {signatures.map((s) => (
                    <Menu.Item key={s.id} onClick={() => onPick(s)}>
                        {s.name}{s.isDefault ? "  ·  default" : ""}
                    </Menu.Item>
                ))}
            </Menu.Content>
        </Menu.Root>
    );
}
